import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useRef, useState } from "react";

interface Debounce<T> {
  value: T;
  /** Immediately set the debounced value, clearing any pending timeout. */
  reset: (value: T) => void;
  /** @deprecated Use `reset` instead — raw setter races with the internal timeout. */
  setDebouncedValue: React.Dispatch<React.SetStateAction<T>>;
  isDebouncing: boolean;
}

const DELAY = 500;

export default function useDebounce<T>(
  value: T,
  delay: number = DELAY
): Debounce<T> {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [value, delay]);

  const reset = useCallback((nextValue: T) => {
    clearTimeout(timeoutRef.current);
    setDebouncedValue(nextValue);
  }, []);

  // Derive during render — effect-based isDebouncing was false for one frame after value
  // changed while debouncedValue was still stale, breaking gates (e.g. report auto-run).
  const isDebouncing = !isEqual(value, debouncedValue);

  return {
    value: debouncedValue,
    reset,
    setDebouncedValue,
    isDebouncing,
  };
}
